import { Button, DatePicker, message, Select } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useState } from 'react';

import PPResizableTable from '../../components/table/PPResizableTable';
import { isHandledByInterceptor } from '../../utils/apiError';
import { getIndexColumn } from '../../utils/getIndexColumn';
import rolesDictionary from '../../config/rolesDictionary';
import ReportFilterBar, { ReportField } from './ReportFilterBar';
import { useCachedState } from './reportCache';
import { findMissingRangeError, getExportUrl, startDownload } from './reportRequest';
import './style.less';

const { RangePicker } = DatePicker;

const ORDER_STATUS_OPTIONS = [
  { label: 'Бүгд', value: 'all' },
  { label: 'Үүсгэсэн', value: 'created' },
  { label: 'Захиалга бүрдсэн', value: 'completed' },
  { label: 'Хүргэлтэнд гарсан', value: 'delivery' },
  { label: 'Бараа хүргэгдсэн', value: 'delivered' },
  { label: 'Цуцалсан', value: 'canceled' },
];

const formatDateTime = (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-');

const columns = [
  { title: 'Захиалгын дугаар', dataIndex: 'parentOrder', key: 'parentOrder', width: 170 },
  { title: 'Барааны нэр', dataIndex: 'productTitle', key: 'productTitle', width: 200 },
  {
    title: 'Үүсгэсэн огноо',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 170,
    render: formatDateTime,
  },
  { title: 'Үүсгэсэн ажилтан', dataIndex: 'requesterName', key: 'requesterName', width: 170 },
  { title: 'Хаяг', dataIndex: 'dropoffDetail', key: 'dropoffDetail', width: 220 },
  { title: 'Захиалгын төлөв', dataIndex: 'orderStatus', key: 'orderStatus', width: 160 },
  { title: 'Хаанаас авсан', dataIndex: 'pickupAddressCode', key: 'pickupAddressCode', width: 150 },
  {
    title: 'Захиалга дууссан огноо',
    dataIndex: 'orderStatusDate',
    key: 'orderStatusDate',
    width: 190,
    render: formatDateTime,
  },
  { title: 'productSKU', dataIndex: 'productSKU', key: 'productSKU', width: 200 },
  { title: 'Барааны баркод', dataIndex: 'productBarCodes', key: 'productBarCodes', width: 180 },
  { title: 'Барааны тоо', dataIndex: 'totalUnit', key: 'totalUnit', width: 120 },
  { title: 'Барааны үнэ', dataIndex: 'totalPrice', key: 'totalPrice', width: 130 },
];

const ProductReport = () => {
  // Таб солиод буцаж ирэхэд хайлтын нөхцөл, үр дүн хэвээр үлдэнэ
  const [range, setRange] = useCachedState('product:range', null);
  const [orderStatus, setOrderStatus] = useCachedState('product:status', 'all');
  const [reports, setReports] = useCachedState('product:reports', []);
  const [pagination, setPagination] = useCachedState('product:pagination', {
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const params = {
    startDate: range?.[0] ? range[0].format('YYYY-MM-DD') : '',
    endDate: range?.[1] ? range[1].format('YYYY-MM-DD') : '',
    orderStatus,
  };

  const getReportData = async (page = 1, pageSize = pagination.pageSize) => {
    // Хугацаагаа бүрэн сонгоогүй үед хайлт явуулахгүй
    const rangeError = findMissingRangeError(range);
    if (rangeError) {
      message.warning(rangeError);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post('/api/v1.0/request/product-report', {
        ...params,
        page,
        pageSize,
      });

      if (res.data?.statusCode === 200) {
        const docs = res.data?.result?.docs || [];
        if (!docs.length) {
          message.warning('Сонгосон хугацаанд мэдээлэл байхгүй байна.');
        }
        setReports(
          docs.map((row) => ({
            ...row,
            orderStatus:
              rolesDictionary.orderStatus.find((role) => role.value === row.orderStatus)?.label ||
              '-',
          }))
        );
        setPagination({ current: page, pageSize, total: res.data.result.totalDocs ?? 0 });
      }
    } catch (error) {
      // Мэдэгдлийг axios-ийн global interceptor гаргана — энд давхардуулахгүй
      console.error('Product report error: ', error);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    const rangeError = findMissingRangeError(range);
    if (rangeError) {
      message.warning(rangeError);
      return;
    }

    setExporting(true);
    try {
      const res = await axios.post('/api/v1.0/request/product-report-excel', params);

      const url = getExportUrl(res);
      // Өгөгдөл байхгүй үед «амжилттай татагдлаа» гэж хэлэхгүй
      if (!url) {
        // Серверийн алдааг interceptor мэдэгдсэн бол «байхгүй» гэж давхардуулахгүй
        if (!isHandledByInterceptor(res.data)) message.warning('Татах тайлан байхгүй байна.');
        return;
      }

      startDownload(url);
      message.success('Excel файл амжилттай татагдлаа.');
    } catch (error) {
      console.error('Product report export error: ', error);
    } finally {
      setExporting(false);
    }
  };

  const handleTableChange = (paginationConfig) =>
    getReportData(paginationConfig.current, paginationConfig.pageSize);

  return (
    <div className="pp_table_wrapper">
      <ReportFilterBar
        onSearch={() => getReportData(1)}
        loading={loading}
        actions={
          <Button
            className="all_buttons"
            icon={<DownloadOutlined />}
            onClick={exportExcel}
            loading={exporting}
          >
            Export
          </Button>
        }
      >
        <ReportField label="Хугацаа сонгоно уу">
          <RangePicker className="range-picker all_buttons" value={range} onChange={setRange} />
        </ReportField>

        <ReportField label="Захиалгын төлөв">
          <Select
            className="select all_buttons"
            value={orderStatus}
            onChange={setOrderStatus}
            options={ORDER_STATUS_OPTIONS}
          />
        </ReportField>
      </ReportFilterBar>

      <PPResizableTable
        rowKey={(record, index) => record?._id ?? index}
        columns={[getIndexColumn(pagination.current, pagination.pageSize), ...columns]}
        dataSource={reports}
        loading={loading}
        size="middle"
        onChange={handleTableChange}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Нийт ${total} илэрц`,
        }}
      />
    </div>
  );
};

export default ProductReport;
